/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-23
 */

import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { ParentResponseSummary } from '../../../types/classEssentials';

interface ParentCentricViewProps {
  data: ParentResponseSummary[];
  loading?: boolean;
}

/**
 * Parent-centric view of Class Essentials responses
 * Shows aggregated data grouped by parent email
 */
export const ParentCentricView: React.FC<ParentCentricViewProps> = ({
  data,
  loading = false,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No parent responses found. Responses will appear here once parents
          complete the checklist.
        </Typography>
      </Paper>
    );
  }

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      already_have: 'Already Have',
      want_to_purchase: 'Want to Purchase',
      purchase_online: 'Purchase Online',
      purchase_in_person: 'Purchase from Studio',
    };
    return labels[status] || status;
  };

  return (
    <Stack spacing={2}>
      {data.map(parent => (
        <Accordion key={parent.email}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ width: '100%', pr: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Box>
                  <Typography variant="h6">
                    {parent.parent_name || parent.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {parent.student_name && `Student: ${parent.student_name}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${parent.total_responses} Responses`}
                    size="small"
                    color="primary"
                  />
                  {parent.studio_purchase_count > 0 && (
                    <Chip
                      label={`${parent.studio_purchase_count} Studio Purchases`}
                      size="small"
                      color="success"
                    />
                  )}
                </Stack>
              </Stack>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Parent Info */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Contact Information
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Email:</strong> {parent.email}
                  </Typography>
                  {parent.parent_name && (
                    <Typography variant="body2">
                      <strong>Parent:</strong> {parent.parent_name}
                    </Typography>
                  )}
                  {parent.student_name && (
                    <Typography variant="body2">
                      <strong>Student:</strong> {parent.student_name}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Preferred Purchase Method:</strong>{' '}
                    {getStatusLabel(parent.preferred_purchase_method)}
                  </Typography>
                </Stack>
              </Box>

              {/* Messages Responded To */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Messages Responded To ({parent.message_titles.length})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {parent.message_titles.map(title => (
                    <Chip key={title} label={title} size="small" />
                  ))}
                </Stack>
              </Box>

              {/* Response Details */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Response Details ({parent.responses.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Message</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parent.responses.map(response => (
                        <TableRow
                          key={`${response.message_title}-${response.item_name}-${response.status}`}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {response.message_title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {response.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(response.status)}
                              size="small"
                              color={
                                response.status === 'purchase_in_person'
                                  ? 'success'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {response.notes || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};
